/**
 * Vague 20 — Wrapper générique d'un groupe (module-onglets)
 *
 * Utilisé par les wrappers Gravités, RisqueConformite, Capital, Cartographies, Origination.
 * Rend une bande d'onglets + le composant enfant correspondant à l'onglet actif.
 *
 * Lit le tab depuis le path (deep linking) via useParams.
 */
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Layers } from "lucide-react";

export default function GroupView({ groupKey, groupLabel, tabs, overline }) {
  const navigate = useNavigate();
  const params = useParams();
  const tabSlug = params.tab || tabs[0]?.slug;

  const current = useMemo(() => tabs.find((t) => t.slug === tabSlug) || tabs[0], [tabs, tabSlug]);

  if (!tabs || tabs.length === 0) {
    return <div className="p-4 text-zinc-500 text-sm">Aucun onglet activé dans cette vue.</div>;
  }

  const Component = current.Component;

  return (
    <div className="space-y-4" data-testid={`group-${groupKey}`}>
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#7F77DD]">
          <Layers className="w-3 h-3" />
          {overline || "Module groupe"}
        </div>
        <h1 className="noam-display text-2xl font-bold mt-1">{groupLabel}</h1>
      </div>

      <div className="flex items-center gap-1 border-b border-white/10 overflow-x-auto" data-testid={`group-${groupKey}-tabs`}>
        {tabs.map((t) => {
          const isActive = t.slug === current.slug;
          return (
            <button
              key={t.slug}
              onClick={() => navigate(`/app/${groupKey}/${t.slug}`)}
              className={`text-xs px-3 py-2 border-b-2 transition whitespace-nowrap ${
                isActive ? "border-[#7F77DD] text-[#7F77DD] font-medium" : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`}
              data-testid={`group-${groupKey}-tab-${t.slug}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div data-testid={`group-${groupKey}-content`}>
        <Component />
      </div>
    </div>
  );
}
